import { useState } from "react";
import { toast } from "sonner";
import { SectionHead, LevelPill, LevelSelect } from "./shared";

export function OverrideEditor({ data, api, member, onChanged, onPick }) {
  const [busy, setBusy] = useState(null);
  const role = member && data.roles.find((r) => r.key === member.role_key);

  const setOverride = async (moduleKey, level) => {
    setBusy(moduleKey);
    try {
      await api.put(`/admin/pioneers/members/${member.id}/override`, { module: moduleKey, level });
      toast.success(level ? `Override set for ${member.name}` : `Override cleared — inherits from ${role?.label}`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set override"); }
    finally { setBusy(null); }
  };

  return (
    <section className="admin-card" data-testid="pioneer-override-editor">
      <SectionHead kicker="Individual override" title="Named-individual permissions"
        action={<select data-testid="pioneer-override-member-select" value={member?.id || ""} onChange={(e) => onPick(data.members.find((m) => m.id === e.target.value) || null)}
          className="bg-[#141414] border border-[#333333] text-[#F5F5F5] text-sm rounded-sm px-3 py-2 focus:border-[#C9A84C] outline-none min-w-[260px]">
          <option value="">— Select a Pioneer —</option>
          {data.members.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.role_label}</option>)}
        </select>} />
      {!member && <p className="text-sm text-[#888888]">Pick a member to grant or restrict a specific module for that person only. The sub-role's defaults stay untouched for everyone else.</p>}
      {member && member.sovereign && <p className="text-sm text-[#D4D4D4]" data-testid="pioneer-override-sovereign-note">{member.name} holds hardcoded full access identical to Admin. Overrides do not apply.</p>}
      {member && member.status === "revoked" && <p className="text-sm text-[#E08A8A]" data-testid="pioneer-override-revoked-note">Access for {member.name} is currently revoked. Reinstate before editing overrides.</p>}
      {member && !member.sovereign && member.status !== "revoked" && (
        <table className="w-full text-left">
          <thead>
            <tr className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#C9A84C]">
              <th className="pb-2 pr-4">Module</th><th className="pb-2 pr-4">{role?.label} default</th><th className="pb-2 pr-4">Override</th><th className="pb-2">Effective</th>
            </tr>
          </thead>
          <tbody>
            {data.modules.map((mod) => {
              const ov = member.overrides?.[mod.key] || null;
              return (
                <tr key={mod.key} className="border-t border-[#262626] hover:bg-[#1A1A1A]" data-testid={`override-row-${mod.key}`}>
                  <td className="py-2 pr-4 text-sm text-[#F5F5F5]">{mod.label}</td>
                  <td className="py-2 pr-4"><LevelPill level={role?.permissions[mod.key]} /></td>
                  <td className="py-2 pr-4"><LevelSelect testid={`override-select-${mod.key}`} value={ov} allowClear disabled={busy === mod.key} onChange={(v) => setOverride(mod.key, v)} /></td>
                  <td className="py-2"><LevelPill level={member.effective[mod.key]} testid={`override-effective-${mod.key}`} />{ov && <span className="ml-2 text-[10px] text-[#C9A84C] uppercase tracking-[0.12em]">overridden</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
